import { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Collapse, Drawer, Input, InputNumber, Space, Spin, Typography } from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  ExperimentOutlined,
  MinusCircleOutlined,
  RobotOutlined,
  ShopOutlined,
  StarOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { getAgentById } from '@/api/agents';
import { getAgentModel } from '@/api/agentModel';
import { getCoverageGaps, type CoverageGapRow } from '@/api/scenarios';
import {
  EvaluationModelRole,
  describeCost,
  formatRates,
  getModelPlan,
  isGenerationFinished,
  startGeneration,
  type EvaluationModelOption,
  type EvaluationModelRoleView,
  type ScenarioGeneration,
} from '@/api/scenarioIntelligence';
import { AgentPickerDrawer } from '@/components/AgentPickerDrawer';
import { ModelPickerDrawer, modelLine } from '@/components/ModelPickerDrawer';
import { StatusPill } from '@/components/StatusPill';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { STAGE_TONE, newRequestId, usePollGeneration } from './generation';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// GenerateScenariosDrawer — Scenario Intelligence SI-05a, mockup 02 (accepted 1 Nov 2026).
// One sentence of concern, the agent and practice preselected, the Scenario model visible with its deployment and price,
// Advanced closed with every default named. Generate starts a durable job; the drawer then follows its stages and may be
// closed. Proposals arrive as drafts — nothing joins the library until a person adds it (the Proposals drawer).
// The agent under test is not chosen here and never changes: its own model is shown as a fact (D-SI-4).
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_BUDGET = 12;

interface AgentFacts {
  id: number;
  displayName: string;
  canonicalName?: string | null;
  modelLine: string | null;
}

export const GenerateScenariosDrawer = observer(function GenerateScenariosDrawer({
  open,
  onClose,
  agentId: initialAgentId,
  initialConcern,
  onOpenProposals,
}: {
  open: boolean;
  onClose: () => void;
  /** Preselected from where the person started; they may change it. */
  agentId: number | null;
  /** A concern to start from (the Overview's "Generate" on an uncovered category); the person may change it. */
  initialConcern?: string | null;
  /** Open the Proposals view on this generation. */
  onOpenProposals?: (agentId: number, generationKey: string) => void;
}) {
  const { themeStore, merchantContextStore: ctx } = useStore();
  const isDark = themeStore.mode === 'dark';
  const merchantId = ctx.selectedMerchantId;
  const practice = merchantId != null ? ctx.merchants.find((m) => m.id === merchantId) : undefined;
  const practiceLabel = practice ? `${practice.title} · #${practice.id}` : merchantId != null ? `Practice #${merchantId}` : null;

  const [agentId, setAgentId] = useState<number | null>(initialAgentId);
  const [agent, setAgent] = useState<AgentFacts | null>(null);
  const [agentError, setAgentError] = useState<string | null>(null);
  const [gaps, setGaps] = useState<CoverageGapRow[]>([]);
  const [concern, setConcern] = useState('');
  const [planRole, setPlanRole] = useState<EvaluationModelRoleView | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);
  const [chosen, setChosen] = useState<EvaluationModelOption | null>(null);
  const [budget, setBudget] = useState<number>(DEFAULT_BUDGET);
  const [pickingModel, setPickingModel] = useState(false);
  const [pickingAgent, setPickingAgent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [requestId, setRequestId] = useState(newRequestId);
  const [started, setStarted] = useState<ScenarioGeneration | null>(null);
  const seq = useRef(0);

  // Each opening starts from where the person came from; a closed drawer keeps nothing stale.
  useEffect(() => {
    if (!open) return;
    setAgentId(initialAgentId);
    setStarted(null);
    setRefusal(null);
    setChosen(null);
    setBudget(DEFAULT_BUDGET);
    setRequestId(newRequestId());
    if (initialConcern) setConcern(initialConcern);
  }, [open, initialAgentId, initialConcern]);

  // The agent and what fits it: its facts and its coverage gaps. Another agent clears what no longer fits.
  useEffect(() => {
    if (!open) return;
    const mine = ++seq.current;
    setAgent(null);
    setAgentError(null);
    setGaps([]);
    if (agentId == null) return;
    Promise.all([getAgentById(agentId), getAgentModel(agentId).catch(() => null)])
      .then(([a, m]) => {
        if (mine !== seq.current) return;
        setAgent({
          id: a.id,
          displayName: a.displayName ?? `Agent #${agentId}`,
          canonicalName: a.canonicalName,
          modelLine: m ? [m.deploymentName ?? m.modelName, m.apiTitle].filter(Boolean).join(' · ') : null,
        });
      })
      .catch((e: Error) => mine === seq.current && setAgentError(e.message));
    getCoverageGaps(agentId)
      .then((rows) => mine === seq.current && setGaps(rows.filter((g) => g.isGap).slice(0, 3)))
      .catch(() => undefined); // suggestions are help, never a reason to block
  }, [open, agentId]);

  // The plan's Scenario model in this practice (D-SI-9): a setting that names a deployment.
  useEffect(() => {
    if (!open) return;
    setPlanRole(null);
    setPlanError(null);
    if (merchantId == null) return;
    getModelPlan(merchantId)
      .then((plan) => setPlanRole(plan.roles.find((r) => r.role === EvaluationModelRole.Scenario) ?? null))
      .catch((e: Error) => setPlanError(e.message));
  }, [open, merchantId]);

  const live = usePollGeneration(started?.key ?? null, merchantId, started);
  const generation = live ?? started;

  const model: EvaluationModelOption | null = chosen ?? planRole?.row ?? null;
  const modelProblem = chosen ? null : planRole?.problem ?? planError;
  const canGenerate = merchantId != null && agentId != null && !!agent && concern.trim().length > 0 && !!model && !submitting;

  const generate = async () => {
    if (!canGenerate || agentId == null) return;
    setSubmitting(true);
    setRefusal(null);
    try {
      const submission = await startGeneration({
        agentId,
        concern: concern.trim(),
        scenarioModelId: chosen?.aIModelId ?? null,
        maxProposals: budget,
        idempotencyKey: requestId,
        merchantId,
      });
      setStarted(submission.generation);
    } catch (e) {
      // Nothing was generated; the concern is kept. A retry with the same request id cannot start a second run.
      setRefusal((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const tryAgain = () => {
    setStarted(null);
    setRequestId(newRequestId());
  };

  const headerBg = isDark ? colors.slate[800] : '#FFFFFF';
  const bodyBg = isDark ? colors.slate[900] : colors.slate[50];
  const borderColor = isDark ? colors.slate[700] : colors.slate[200];
  const muted = isDark ? colors.slate[400] : colors.slate[500];
  const section = { background: headerBg, border: `1px solid ${borderColor}`, borderRadius: 10, padding: 16 } as const;

  const title = started ? 'Generating scenarios' : 'Generate scenarios';
  const subtitle = started
    ? [generation?.agentCanonicalName, generation?.scenarioModel?.deploymentName, generation ? `started ${dayjs(generation.requestedAtUtc).format('HH:mm')}` : null]
        .filter(Boolean)
        .join(' · ')
    : 'Proposals arrive as drafts. Nothing joins the library until you add it.';

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      size="min(640px, 100vw)"
      destroyOnHidden
      title={
        <div>
          <Space size={8} align="center">
            <StarOutlined style={{ color: colors.violet[600] }} />
            <span>{title}</span>
          </Space>
          <Text type="secondary" style={{ display: 'block', fontSize: 12, fontWeight: 400 }}>{subtitle}</Text>
        </div>
      }
      styles={{ body: { padding: 16, background: bodyBg } }}
      footer={
        started ? (
          <Space style={{ width: '100%', justifyContent: 'flex-end' }} wrap>
            {generation?.state === 'Failed' && <Button onClick={tryAgain}>Try again</Button>}
            <Button onClick={onClose}>Close</Button>
            {generation && agentId != null && !(isGenerationFinished(generation) && generation.proposalCount === 0) && (
              <Button
                type="primary"
                disabled={!isGenerationFinished(generation) && generation.proposalCount === 0}
                onClick={() => onOpenProposals?.(agentId, generation.key)}
              >
                {generation.state === 'Completed' ? `Open proposals (${generation.proposalCount})` : 'Open proposals'}
              </Button>
            )}
          </Space>
        ) : (
          <Space style={{ width: '100%', justifyContent: 'flex-end' }} wrap>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" icon={<StarOutlined />} loading={submitting} disabled={!canGenerate} onClick={() => void generate()}>
              Generate
            </Button>
          </Space>
        )
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {started ? (
        <GenerationProgress generation={generation} isDark={isDark} />
      ) : (
        <>
          {refusal && (
            <Alert
              type="error"
              showIcon
              title="Nothing was generated"
              description={
                <>
                  <div>{refusal}</div>
                  <div style={{ marginTop: 6 }}>{`Your concern is kept: “${concern.trim()}”`}</div>
                </>
              }
              action={<Button size="small" onClick={() => setPickingModel(true)}>Choose another model…</Button>}
            />
          )}

          {/* 1 · Agent and practice */}
          <div style={section}>
            <SectionHead n={1} title="Agent and practice" hint={initialAgentId != null ? 'Set by where you started' : undefined} isDark={isDark} />
            {agentId == null ? (
              <Alert type="info" showIcon title="Choose the agent these scenarios test." />
            ) : agentError ? (
              <Alert type="error" showIcon title="Could not read the agent" description={agentError} />
            ) : !agent ? (
              <Spin size="small" />
            ) : (
              <FactRow
                icon={<RobotOutlined />}
                title={`${agent.displayName}${agent.canonicalName ? ` · ${agent.canonicalName}` : ''} · #${agent.id}`}
                meta={
                  agent.modelLine
                    ? `Runs on ${agent.modelLine}, from its own configuration. Generating scenarios never changes it.`
                    : 'Runs on its own configured model. Generating scenarios never changes it.'
                }
                isDark={isDark}
              />
            )}
            <div style={{ height: 8 }} />
            {practiceLabel ? (
              <FactRow
                icon={<ShopOutlined />}
                title={practiceLabel}
                meta="Scenarios belong to the platform; generation and its runs happen here."
                isDark={isDark}
              />
            ) : (
              <Alert type="warning" showIcon title="Choose a practice first: the Scenario model runs on the practice’s own registered deployments." />
            )}
            <Button size="small" style={{ marginTop: 10 }} onClick={() => setPickingAgent(true)}>
              {agentId == null ? 'Choose agent…' : 'Change agent…'}
            </Button>
          </div>

          {/* 2 · The concern */}
          <div style={section}>
            <SectionHead n={2} title="What should these scenarios test?" isDark={isDark} />
            <Input.TextArea
              aria-label="What should these scenarios test?"
              value={concern}
              onChange={(e) => setConcern(e.target.value)}
              autoSize={{ minRows: 2, maxRows: 5 }}
              maxLength={1000}
              placeholder="Test how this agent handles missing information and failed follow-up."
            />
            <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 6 }}>
              One sentence is enough. Name a worry, a responsibility or a gap; the budget and sources use the defaults below.
            </Text>
            {gaps.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>From coverage gaps:</Text>
                {gaps.map((g) => (
                  <Button key={g.code} size="small" type="dashed" onClick={() => setConcern(`Test how this agent handles ${g.label.toLowerCase()}.`)}>
                    {`✦ ${g.label}`}
                    <span style={{ color: muted, marginLeft: 6 }}>{`${g.count} scenario${g.count === 1 ? '' : 's'}`}</span>
                  </Button>
                ))}
              </div>
            )}
          </div>

          {/* 3 · The Scenario model */}
          <div style={section}>
            <SectionHead n={3} title="Scenario model" isDark={isDark} />
            {model ? (
              <ModelFact option={model} source={chosen ? 'Chosen for this generation only' : 'The evaluation model plan’s setting'} isDark={isDark} />
            ) : modelProblem ? (
              <Alert type="warning" showIcon title="The Scenario model cannot run in this practice" description={modelProblem} />
            ) : merchantId != null ? (
              <Spin size="small" />
            ) : null}
            <Space style={{ marginTop: 10 }} wrap>
              <Button size="small" onClick={() => setPickingModel(true)} disabled={merchantId == null}>
                Change…
              </Button>
              {chosen && (
                <Button size="small" type="link" onClick={() => setChosen(null)}>
                  Use the plan’s setting
                </Button>
              )}
            </Space>
            <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 6 }}>
              {`Drafts the proposals. It never answers as the agent: ${agent?.canonicalName ?? 'the agent'} runs on its own model when the scenarios run.`}
            </Text>
          </div>

          {/* 4 · Advanced, closed */}
          <Collapse
            ghost={false}
            style={{ background: headerBg, borderColor }}
            items={[
              {
                key: 'advanced',
                label: (
                  <div>
                    <Text strong>Advanced settings</Text>
                    <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                      {`Defaults: the agent’s skills, knowledge, briefings, peers and tools · its library as worked examples · up to ${budget} proposals · no critic model.`}
                    </Text>
                  </div>
                ),
                children: (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <Space wrap>
                      <Text>Case budget</Text>
                      <InputNumber aria-label="Case budget" min={1} max={24} value={budget} onChange={(v) => setBudget(typeof v === 'number' ? v : DEFAULT_BUDGET)} />
                      <Text type="secondary" style={{ fontSize: 12 }}>proposals at most; paraphrases are grouped and never count twice</Text>
                    </Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Critic model: off. A second model’s critique, a reviewed requirement set and coverage planning are not built yet.
                    </Text>
                  </div>
                ),
              },
            ]}
          />

          <div
            style={{
              fontSize: 12.5, padding: '10px 12px', borderRadius: 8,
              border: `1px solid ${isDark ? colors.violet[700] : colors.violet[200]}`,
              background: isDark ? colors.violet[700] + '1f' : colors.violet[50],
            }}
          >
            <b>Every expected behavior is a proposal.</b> Clinical ones carry “Pending SME review”. A proposal that rests on
            something unsettled — an unreviewed requirement, missing context, a figure no source gives — comes back as an
            incomplete draft that names the gap and cannot run.
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {costLine(model)}
          </Text>
        </>
      )}

      </div>

      <AgentPickerDrawer
        open={pickingAgent}
        onClose={() => setPickingAgent(false)}
        selectedId={agentId}
        onSelect={(id) => {
          setAgentId(id);
          setPickingAgent(false);
        }}
        title="Generate scenarios for…"
      />
      <ModelPickerDrawer
        open={pickingModel}
        onClose={() => setPickingModel(false)}
        role={EvaluationModelRole.Scenario}
        merchantId={merchantId}
        practiceLabel={practiceLabel ?? undefined}
        selectedId={model?.aIModelId ?? null}
        title="Scenario model for this generation"
        onSelect={(o) => {
          setChosen(o);
          setRefusal(null);
        }}
      />
    </Drawer>
  );
});

/** How the cost is reported — honestly, before anything has run. */
function costLine(model: EvaluationModelOption | null): string {
  if (!model) return 'Generation runs as a background job; you can close this drawer and find the proposals under Proposals.';
  const name = model.deployment ?? model.name;
  const rates = formatRates(model);
  const price = rates
    ? `priced at ${rates} per 1M tokens in / out`
    : 'which has no price: its calls are recorded as unpriced, never as $0';
  return `Runs as a background job on ${name} (${price}): one model call, and one more only if the first answer is not a scenario set. The recorded cost appears with the proposals; you can close this drawer.`;
}

/** Mockup 02, frame D1: the stages of a running generation, said plainly. */
function GenerationProgress({ generation, isDark }: { generation: ScenarioGeneration | null; isDark: boolean }) {
  if (!generation) return <Spin />;
  const border = isDark ? colors.slate[700] : colors.slate[200];
  const cost = describeCost(generation.cost);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {generation.state === 'Failed' && (
        <Alert
          type="error"
          showIcon
          title="The generation failed: nothing was saved as a proposal"
          description={
            <>
              <div>{generation.stateReason}</div>
              {generation.failedAttemptReasons.map((r) => (
                <div key={r} style={{ fontSize: 12, marginTop: 4 }}>{r}</div>
              ))}
            </>
          }
        />
      )}
      {generation.state === 'Completed' && (
        <Alert
          type="success"
          showIcon
          title={`${generation.proposalCount} proposal${generation.proposalCount === 1 ? '' : 's'} ready to review`}
          description={`${generation.runnableCount} runnable, ${generation.incompleteCount} incomplete, ${generation.paraphrasesGrouped} paraphrase${generation.paraphrasesGrouped === 1 ? '' : 's'} grouped. Nothing has joined the library.`}
        />
      )}
      <div style={{ background: isDark ? colors.slate[800] : '#FFFFFF', border: `1px solid ${border}`, borderRadius: 10 }}>
        {generation.stages.map((s, i) => (
          <div key={s.key} style={{ display: 'flex', gap: 12, padding: '12px 16px', borderTop: i === 0 ? 'none' : `1px solid ${border}` }}>
            <div style={{ width: 22, textAlign: 'center', paddingTop: 2 }}>
              {s.status === 'Done' ? (
                <CheckCircleFilled style={{ color: colors.green[600] }} />
              ) : s.status === 'Failed' ? (
                <CloseCircleFilled style={{ color: colors.red[600] }} />
              ) : s.status === 'Running' ? (
                <Spin size="small" />
              ) : (
                <MinusCircleOutlined style={{ color: colors.slate[400] }} />
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Space size={8} wrap>
                <Text strong>{s.title}</Text>
                <StatusPill tone={STAGE_TONE[s.status] ?? 'slate'}>{s.status === 'Later' ? 'Not built yet' : s.status}</StatusPill>
              </Space>
              <div style={{ fontSize: 12.5, color: isDark ? colors.slate[300] : colors.slate[600], marginTop: 2 }}>{s.detail}</div>
            </div>
          </div>
        ))}
      </div>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {isGenerationFinished(generation)
          ? cost ?? 'No model call was recorded for this generation.'
          : `You can close this drawer. Generation continues as a job and the proposals appear under Proposals.${cost ? ` ${cost} so far.` : ''}`}
      </Text>
    </div>
  );
}

function SectionHead({ n, title, hint }: { n: number; title: string; hint?: string; isDark?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
      <span
        style={{
          width: 22, height: 22, borderRadius: 11, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: colors.ink[600], color: '#FFFFFF', fontSize: 12, fontWeight: 700,
        }}
      >
        {n}
      </span>
      <Text strong style={{ fontSize: 14 }}>{title}</Text>
      {hint && <StatusPill tone="slate">{hint}</StatusPill>}
    </div>
  );
}

function FactRow({ icon, title, meta, isDark }: { icon: React.ReactNode; title: string; meta: string; isDark: boolean }) {
  return (
    <div
      style={{
        display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 8,
        background: isDark ? colors.ink[700] + '26' : colors.ink[50], borderLeft: `3px solid ${colors.ink[600]}`,
      }}
    >
      <span style={{ color: colors.ink[600], paddingTop: 2 }}>{icon}</span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{title}</div>
        <div style={{ fontSize: 12, color: isDark ? colors.slate[300] : colors.slate[600] }}>{meta}</div>
      </div>
      <CheckCircleFilled style={{ color: colors.ink[600], marginLeft: 'auto', paddingTop: 3 }} />
    </div>
  );
}

function ModelFact({ option, source, isDark }: { option: EvaluationModelOption; source: string; isDark: boolean }) {
  const rates = formatRates(option);
  const scope = option.merchantId == null ? 'every practice' : `practice #${option.merchantId}`;
  const line = useMemo(() => modelLine(option), [option]);
  return (
    <div
      style={{
        display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 8,
        background: isDark ? colors.violet[700] + '26' : colors.violet[50], borderLeft: `3px solid ${colors.violet[600]}`,
      }}
    >
      <ExperimentOutlined style={{ color: colors.violet[600], paddingTop: 3 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
          {option.title ?? option.name}{' '}
          {(option.deployment ?? option.name) !== (option.title ?? option.name) && (
            <code style={{ fontSize: 12, fontWeight: 500 }}>{option.deployment ?? option.name}</code>
          )}
        </div>
        <div style={{ fontSize: 12, color: isDark ? colors.slate[300] : colors.slate[600] }}>
          {[line?.split(' · ')[1] ?? option.apiTitle, scope, source].filter(Boolean).join(' · ')}
        </div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
        {rates ? (
          <>
            <div style={{ fontSize: 12.5 }}>{rates}</div>
            <div style={{ fontSize: 11, color: isDark ? colors.slate[400] : colors.slate[500] }}>in / out per 1M tokens</div>
          </>
        ) : (
          <StatusPill tone="slate">Price unavailable</StatusPill>
        )}
      </div>
    </div>
  );
}
